import { useMemo, useRef } from 'react';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';

import { Breadcrumb2 } from '~/components';

import { Header, Headers2 } from '../Header';

import { useTranslation } from 'react-i18next';

export default function IntegrLayout(props: {
  integrType: string,
  integrId: string,
  varsCfg?: any,
}): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation();

  const { integrType, integrId, varsCfg } = props;
  const isEdit = history.location.pathname.endsWith('/editar');

  function checkHistoryVisibility() {
    const excludedApplications = [
      'cg-et330', 'abb-nexus-ii', 'abb-ete-30', 'abb-ete-50',
      'cg-em210', 'kron-mult-k', 'kron-mult-k-05', 'kron-mult-k-120',
      'kron-ikron-03', 'schneider-eletric-pm2100', 'cg-em330',
      'schneider-electric-pm210', 'schneider-electric-pm9c', 'abb-m1m-20',
    ];

    const isValidDielApp = integrType === 'diel'
      && varsCfg?.application
      && !excludedApplications.includes(varsCfg.application);

    const isSpecialType = ['water', 'coolautomation'].includes(integrType);
    const isDielWithDMA = integrType === 'diel' && integrId.startsWith('DMA');

    return isValidDielApp || isSpecialType || isDielWithDMA;
  }
  function checkRealTimeVisibility() {
    const isDielWithVars = integrType === 'diel' && varsCfg;
    const isSpecialType = ['water', 'coolautomation'].includes(integrType);
    const isDielWithDMA = integrType === 'diel' && integrId.startsWith('DMA');

    return isDielWithVars || isSpecialType || isDielWithDMA;
  }

  function isSplitaoInverterHitachi() {
    return varsCfg?.application === 'splitao-inverter-hitachi';
  }

  const links = [
    {
      title: t('perfil'),
      link: `/integracoes/info/${integrType}/${integrId}/perfil`,
      visible: true,
      ref: useRef(null),
    },
    {
      title: t('tempoReal'),
      link: `/integracoes/info/${integrType}/${integrId}/tempo-real`,
      visible: checkRealTimeVisibility(),
      ref: useRef(null),
    },
    {
      title: t('historico'),
      link: `/integracoes/info/${integrType}/${integrId}/historico`,
      visible: checkHistoryVisibility(),
      ref: useRef(null),
    },
    {
      title: t('editar'),
      link: `/integracoes/info/${integrType}/${integrId}/editar`,
      visible: !!isEdit,
      ref: useRef(null),
    },
  ];

  return (
    <Header links={links.filter((x) => x.visible)} match={history} />
  );
}

export function buildTabLink(aba: string, history: ReturnType<typeof useHistory>) {
  const queryPars = queryString.parse(history.location.search);
  const linkBase = history.location.pathname;

  return `${linkBase}?${queryString.stringify({ ...queryPars, aba })}`;
}

export function IntegrInlineTabs(props: {
  integrType: string,
}): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { integrType } = props;

  const queryPars = queryString.parse(history.location.search);
  const linkBase = history.location.pathname;

  const allTabs = [
    {
      title: t('perfil'),
      link: `${linkBase}?${queryString.stringify({ ...queryPars, aba: 'perfil' })}`,
      visible: true,
      isActive: (queryPars.aba === 'perfil') || (!queryPars.aba && integrType !== 'coolautomation'),
      ref: useRef(null),
    },
    {
      title: t('tempoReal'),
      link: `${linkBase}?${queryString.stringify({ ...queryPars, aba: 'tempo-real' })}`,
      visible: (['diel', 'coolautomation'].includes(integrType) || (integrType === 'water' && queryPars.supplier === 'diel')),
      isActive: (queryPars.aba === 'tempo-real') || (!queryPars.aba && integrType === 'coolautomation'),
      ref: useRef(null),
    },
    {
      title: t('historico'),
      link: `${linkBase}?${queryString.stringify({ ...queryPars, aba: 'historico' })}`,
      visible: (['diel', 'water', 'coolautomation'].includes(integrType)),
      isActive: (queryPars.aba === 'historico'),
      ref: useRef(null),
    },
    {
      title: t('editar'),
      link: `${linkBase}?${queryString.stringify({ ...queryPars, aba: 'editar' })}`,
      visible: (queryPars.aba === 'editar'),
      isActive: (queryPars.aba === 'editar'),
      ref: useRef(null),
    },
  ];

  return (
    <Headers2 links={allTabs.filter((x) => x.visible)} />
  );
}

export function IntegrBreadCrumbs(props: {
  integrId: string,
  integrType: string,
  margin?: string,
  devInfo: null | {
    CLIENT_ID: number,
    CLIENT_NAME: string,
    UNIT_NAME: string,
    UNIT_ID: number,
    machineName: string,
    machineId: number | null,
    dataSource: string,
    automatedMachine?: string,
    TIMEZONE_ID?: number
    TIMEZONE_AREA?: string
    TIMEZONE_OFFSET?: number
  },
}) {
  const { t } = useTranslation();
  const { integrId, devInfo, margin } = props;
  const integrType = props.integrType === 'water' ? t('agua') : props.integrType;

  const bcrumbs = useMemo(() => {
    const path = [] as {}[];
    // if (devInfo && devInfo.UNIT_NAME) {
    //   path.push({ text: 'Unidades', link: '/analise/unidades' });
    //   path.push({ text: devInfo.UNIT_NAME, link: `/analise/unidades/${devInfo.UNIT_ID}` });
    //   if (devInfo.machineName) {
    //     path.push({ text: devInfo.machineName, link: null });
    //   }
    // }
    // path.push({ text: `Integração ${integrType}`, link: `/integracoes?fornecedor=${integrType}` });
    if (integrType === 'diel' && devInfo && devInfo.CLIENT_ID && devInfo.UNIT_ID) {
      path.push({ text: devInfo.CLIENT_NAME, link: '/visao-geral' });
      path.push({ text: devInfo.UNIT_NAME, link: `/analise/unidades/${devInfo.UNIT_ID}` });
      if (devInfo.machineName) {
        path.push({ text: devInfo.machineName, link: `/integracoes/info/diel/${devInfo.dataSource}/perfil` });
      }
      else if (devInfo.automatedMachine) {
        path.push({ text: devInfo.automatedMachine, link: `/analise/maquina/${devInfo.machineId}/ativos` });
      }
      path.push({ text: devInfo.dataSource, link: null });
    } else if (devInfo) {
      path.push({ text: t('integracoes'), link: '/integracoes' });
      path.push({ text: integrType.toUpperCase(), link: `/integracoes?fornecedor=${integrType}` });
      path.push({ text: (devInfo && devInfo.dataSource) || integrId, link: null }); // match.url
    }
    return (<Breadcrumb2 items={path} timezoneArea={devInfo?.TIMEZONE_AREA} timezoneGmt={devInfo?.TIMEZONE_OFFSET} unitId={devInfo?.UNIT_ID} />);
  }, [devInfo]);
  return (
    <>
      {bcrumbs}
      <div style={{ marginTop: margin || '35px' }} />
    </>
  );
}
